import { type ReactNode } from 'react';
import { PublicShell } from '../components/PublicShell';
import { useEnter } from '../lib/motion';

/**
 * Pflichtseiten.
 *
 * ENTWÜRFE. Die Struktur folgt § 5 DDG und Art. 13 DSGVO, die Inhalte bilden
 * den tatsächlichen technischen Aufbau ab. Die mit AUSFÜLLEN markierten
 * Stellen müssen ersetzt werden, bevor die Seite beworben oder öffentlich
 * verlinkt wird. Eine anwaltliche Prüfung ersetzt das nicht.
 */

function Rahmen({ titel, children }: { titel: string; children: ReactNode }) {
  const ref = useEnter<HTMLDivElement>([titel]);
  return (
    <PublicShell>
      <div ref={ref} className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <h1 className="text-3xl font-semibold tracking-tight">{titel}</h1>
        <div className="mt-8 flex flex-col gap-8">{children}</div>
      </div>
    </PublicShell>
  );
}

function Abschnitt({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-lg font-semibold tracking-tight">{titel}</h2>
      <div className="mt-2 flex flex-col gap-2 text-[15px] leading-relaxed text-[var(--text-muted)]">{children}</div>
    </section>
  );
}

export function ImpressumPage() {
  return (
    <Rahmen titel="Impressum">
      <Abschnitt titel="Angaben gemäß § 5 DDG">
        <p>
          Jonas Fischer
          <br />
          Erftstraße 13-15
          <br />
          41460 Neuss
          <br />
          Deutschland
        </p>
      </Abschnitt>

      <Abschnitt titel="Kontakt">
        <p>
          Telefon: <a className="underline" href="tel:+491732980110">+49 173 29 80 110</a>
          <br />
          E-Mail: <a className="underline" href="mailto:kontakt@jif-works.de">kontakt@jif-works.de</a>
        </p>
      </Abschnitt>

      <Abschnitt titel="Umsatzsteuer">
        <p>
          Eine Umsatzsteuer-Identifikationsnummer nach § 27 a Umsatzsteuergesetz liegt nicht vor. Die Angabe ist nach
          § 5 Abs. 1 Nr. 6 DDG nur zu machen, soweit eine solche Nummer vorhanden ist.
        </p>
      </Abschnitt>

      <Abschnitt titel="Verantwortlich für den Inhalt">
        <p>Jonas Fischer, Anschrift wie oben.</p>
      </Abschnitt>

      <Abschnitt titel="Streitbeilegung">
        <p>
          Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer
          Verbraucherschlichtungsstelle teilzunehmen.
        </p>
      </Abschnitt>

      <Abschnitt titel="Haftung für Inhalte und Links">
        <p>
          Für eigene Inhalte auf diesen Seiten sind wir nach den allgemeinen Gesetzen verantwortlich. Für verlinkte
          fremde Inhalte ist der jeweilige Anbieter verantwortlich. Zum Zeitpunkt der Verlinkung waren keine
          rechtswidrigen Inhalte erkennbar. Werden uns Rechtsverletzungen bekannt, entfernen wir den Link umgehend.
        </p>
      </Abschnitt>
    </Rahmen>
  );
}

export function DatenschutzPage() {
  return (
    <Rahmen titel="Datenschutzerklärung">
      <Abschnitt titel="Verantwortlicher">
        <p>
          Jonas Fischer, Erftstraße 13-15, 41460 Neuss,{' '}
          <a className="underline" href="mailto:kontakt@jif-works.de">kontakt@jif-works.de</a>
        </p>
        <p>
          Ein Datenschutzbeauftragter ist nicht benannt. Die Voraussetzungen des Art. 37 DSGVO und des § 38 BDSG
          liegen nicht vor.
        </p>
      </Abschnitt>

      <Abschnitt titel="Diese Website">
        <p>
          Diese Website setzt <strong>keine Cookies zu Analyse- oder Werbezwecken</strong>. Es findet keine
          Reichweitenmessung statt, es sind keine Dienste Dritter eingebunden. Schriften und Bilder werden von
          unserem eigenen Server ausgeliefert, nicht über fremde Netze.
        </p>
        <p>
          Beim Aufruf verarbeitet unser Hoster technisch notwendige Verbindungsdaten, darunter die IP-Adresse, um die
          Seite ausliefern zu können. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO, unser berechtigtes Interesse am
          Betrieb der Website.
        </p>
      </Abschnitt>

      <Abschnitt titel="Anmeldung am Dashboard">
        <p>
          Wer ein Konto hat, kann sich anmelden. Dabei wird ein technisch notwendiges Sitzungscookie{' '}
          <code className="tabular">ilc_session</code> gesetzt. Es enthält eine Zufallskennung, keine
          personenbezogenen Inhalte, und läuft nach 14 Tagen ab. Ohne dieses Cookie ist keine Anmeldung möglich,
          deshalb ist dafür keine Einwilligung erforderlich (§ 25 Abs. 2 Nr. 2 TDDDG).
        </p>
        <p>
          Gespeichert werden E-Mail-Adresse, Name soweit angegeben, das Passwort ausschließlich als Hashwert, die
          Rollenzuordnung, der Zeitpunkt der letzten Anmeldung sowie zu jeder Sitzung ein Hashwert aus IP-Adresse und
          Browserkennung. Die IP-Adresse selbst wird nicht gespeichert.
        </p>
      </Abschnitt>

      <Abschnitt titel="Einwilligungsnachweise unserer Kunden">
        <p>
          Wenn Sie hier gelandet sind, weil Sie auf einer anderen Website ein Banner von ilovecookies gesehen haben:
          für diese Verarbeitung ist der <strong>Betreiber jener Website</strong> verantwortlich, nicht wir. Wir
          verarbeiten die Daten dort ausschließlich in dessen Auftrag nach Art. 28 DSGVO.
        </p>
        <p>
          Gespeichert wird je Entscheidung: eine im Browser erzeugte Zufallskennung, die Entscheidung je
          Zweckkategorie, der Zeitpunkt, die Seite ohne Suchparameter, die Sprache, ein Länderkürzel, eine grobe
          Browserfamilie sowie ein Hashwert, in den die IP-Adresse zusammen mit einem serverseitigen Geheimnis
          eingeht. <strong>Die IP-Adresse wird nicht gespeichert.</strong>
        </p>
        <p>
          Die Aufbewahrungsdauer legt der jeweilige Websitebetreiber fest, voreingestellt sind 24 Monate. Danach
          werden die Einträge automatisch gelöscht.
        </p>
      </Abschnitt>

      <Abschnitt titel="Empfänger und Speicherort">
        <p>
          Die Anwendung läuft bei <strong>Cloudflare, Inc.</strong> Die Datenbank betreiben wir bei{' '}
          <strong>Neon, Inc.</strong>, seit 2025 Teil von Databricks, Inc., in der Region Frankfurt am Main. Beide
          verarbeiten die Daten ausschließlich in unserem Auftrag auf Grundlage eines Vertrags nach Art. 28 DSGVO.
          Soweit ein Zugriff aus einem Drittland nicht ausgeschlossen werden kann, stützen wir die Übermittlung auf
          die Standardvertragsklauseln der Europäischen Kommission. Eine Kopie dieser Garantien können Sie bei uns
          anfordern (Art. 13 Abs. 1 lit. f DSGVO).
        </p>
      </Abschnitt>

      <Abschnitt titel="Ihre Rechte">
        <p>
          Sie haben das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der
          Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch (Art. 21 DSGVO). Eine erteilte
          Einwilligung können Sie jederzeit mit Wirkung für die Zukunft widerrufen.
        </p>
        <p>
          Zu Einwilligungsnachweisen: eine Zuordnung zu Ihrer Person ist uns ohne die in Ihrem Browser gespeicherte
          Kennung <code className="tabular">ilc_consent</code> nicht möglich. Nach Art. 11 Abs. 2 DSGVO sind wir nicht
          verpflichtet, zusätzliche Daten allein zu Ihrer Identifizierung aufzubewahren. Teilen Sie uns die Kennung
          mit, können wir die zugehörigen Einträge zuordnen.
        </p>
        <p>
          Sie können sich bei einer Aufsichtsbehörde beschweren, in der Regel bei der für Ihren Wohnsitz zuständigen
          Landesbeauftragten für Datenschutz.
        </p>
      </Abschnitt>

      <Abschnitt titel="Stand">
        <p>September 2026</p>
      </Abschnitt>
    </Rahmen>
  );
}
